import { toast } from "sonner";
import { useSavedKitKeys } from "@/hooks/useSavedKits";

/**
 * Save / un-save button for any channel kit.
 *
 * Pass a snapshot of the kit so we can show it on the Saved Kits page
 * without re-fetching from the source flow (auto-video job / niche).
 *
 * Props:
 *   kit_key:     stable string (auto-video job_id, niche_id, or kit_id)
 *   source_type: "create_channel" | "auto_video"
 *   name:        display name for the saved list
 *   ... rest is snapshotted into the saved_kits doc
 */
export default function SaveKitButton({
  kit_key,
  source_type,
  source_id,
  channel_id,
  channel_name,
  niche,
  name,
  data,
  images,
  size = "md",
}) {
  const { keys, toggle } = useSavedKitKeys();
  const isSaved = keys.has(kit_key);
  const sizeClasses =
    size === "sm"
      ? "text-[10px] px-2 py-1.5"
      : "text-xs px-3 py-2";

  return (
    <button
      data-testid={`save-kit-btn-${kit_key}`}
      data-saved={isSaved ? "true" : "false"}
      title={isSaved ? "Saved — click to remove" : "Save this channel kit"}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          const nowSaved = await toggle({
            kit_key,
            source_type,
            source_id,
            channel_id,
            channel_name,
            niche,
            name,
            data,
            images,
          });
          toast.success(nowSaved ? `Saved · ${name || "kit"}` : `Removed · ${name || "kit"}`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Couldn't save kit");
        }
      }}
      className={`font-mono ${sizeClasses} uppercase tracking-[0.15em] rounded-sm transition-colors leading-tight border ${
        isSaved
          ? "bg-yellow-400 text-black border-yellow-400 hover:bg-yellow-300 font-bold"
          : "text-yellow-400 border-yellow-400/40 hover:bg-yellow-400/10"
      }`}
    >
      {isSaved ? "★ Kit saved" : "☆ Save kit"}
    </button>
  );
}
